import { h } from 'preact';
import Store from 'store/store';
import { joinClasses } from 'utils/utils';
import * as jsonPointer from 'utils/jsonPointer';
import { getIcon } from 'utils/icons';

import FieldBase from 'components/editor/settings/fieldBase';
import Dropdown from 'components/dropdown/dropdown';
import Toggle from 'components/toggle/toggle';

import s from 'components/editor/settings/linkField.sss';

export default class LinkField extends FieldBase {
	s = s;

	constructor(props) {
		super(props);

		const initialValue = (this.isInheriting && this.inheritedValue) || this.value;
		const isExternal = !this.getOption(initialValue);
		this.state = {
			...this.state,
			isExternal,
		};

		this.onInput = this.onInput.bind(this);
		this.toggleExternal = this.toggleExternal.bind(this);
		this.internalDropdownRef = this.internalDropdownRef.bind(this);
		this.externalInputRef = this.externalInputRef.bind(this);
	}

	get labelFor() {
		return null;
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.nextValue != null) {
			const input = this.$internalDropdown || this.$externalInput;
			if (input) input.value = this.nextValue;
			this.nextValue = null;
		}

		if (this.isInheriting) {
			const isExternal = !this.getOption(this.inheritedValue) || false;
			if (isExternal !== this.state.isExternal) {
				this.setState({ isExternal });
			}
		}
	}

	getOption(value) {
		return value && this.props.options?.find(option => option.destination === value);
	}

	onInput(e) {
		// NOTE: should we automatically add missing https:// for external links ?
		this.props.onInput?.(e);
		if (!this.props.path) return;
		jsonPointer.setDeep(Store.get().data, this.props.path, e.target.value);
	}

	toggleExternal(e) {
		const isExternal = e.currentTarget.checked;
		if (isExternal === this.state.isExternal) return;
		if (isExternal) {
			this.lastPageValue = this.$internalDropdown?.value;
			this.nextValue = this.lastExternalValue || '';
		} else {
			this.lastExternalValue = this.$externalInput?.value;
			this.nextValue = this.lastPageValue || '';
		}
		this.onInput({ target: { value: this.nextValue } });
		this.setState({ isExternal });
	}

	internalDropdownRef(e) {
		this.$internalDropdown = e;
	}
	externalInputRef(e) {
		this.$externalInput = e;
	}

	renderInternalDropdown() {
		const { dark, options } = this.props;
		const { id } = this.state;
		const value = this.value;

		const placeholderOption = this.getOption(this.placeholder);
		const placeholder = placeholderOption && (placeholderOption.name || placeholderOption.label) || 'Select a destination...';

		return (
			<Dropdown
				dark={dark}
				name={`${id}-internal-options`}
				class={joinClasses(s.field, s.dropdown)}
				icon="file"
				align="full"
				value={value}
				allowEmpty
				placeholder={placeholder}
				disabled={this.disabled || this.readonly}
				onChange={this.onInput}
				ref={this.internalDropdownRef}
				key="internal"
			>
				{options?.map(option => (
					<option value={option.destination || option.path} class={s.option} disabled={option.disabled}>
						{option.name || option.label}
					</option>
				))}
			</Dropdown>
		);
	}

	renderExternalInput() {
		const { name, required } = this.props;
		const value = this.value;

		const placeholderOption = this.getOption(this.placeholder);
		const placeholder = !placeholderOption && this.placeholder || 'https://example.com';

		return (
			<div class={s.field}>
				{getIcon('link')}
				<input
					type="url"
					name={name}
					// Do not use the value attribute if uncontrolled to avoid resetting on render
					// Value is controlled if a path is provided or if a value prop is provided
					{...(value != undefined || this.props.path ? { value: value ?? '' } : {})}
					onInput={this.onInput}
					placeholder={placeholder}
					required={required}
					readonly={this.readonly}
					disabled={this.disabled}
					ref={this.externalInputRef}
					key="external"
				/>
			</div>
		);
	}

	renderField() {
		const { dark } = this.props;
		const { isExternal } = this.state;

		return [
			!isExternal
				? this.renderInternalDropdown()
				: this.renderExternalInput(),
			<Toggle
				dark={dark}
				class={s.toggle}
				checked={isExternal}
				onInput={this.toggleExternal}
				disabled={this.disabled || this.readonly}
				key="toggle"
			>
				External link
			</Toggle>
		];
	}
}